import { type CSSProperties } from "react";
import { Button } from "@aisquare/ui";
import { PlusIcon, ChevronRightIcon } from "lucide-react";
import { Section, VariantBlock } from "../lib/Section";

/**
 * Pin --primary to the brand navy (+ a light foreground) so the primary key
 * reads as the rich navy gradient in BOTH themes — the lib's shadcn --primary
 * inverts to near-white under .dark, which would wash the key out. The unified
 * adoption makes the same pin (handoff §1c). `display:contents` scopes the var
 * to the buttons without changing VariantBlock's flex layout.
 */
const NAVY_PRIMARY = {
  "--primary": "212 72% 16%",
  "--primary-foreground": "0 0% 100%",
} as CSSProperties;

const Keyed = ({ children }: { children: React.ReactNode }) => (
  <div className="contents" style={NAVY_PRIMARY}>
    {children}
  </div>
);

export function ButtonDemo() {
  return (
    <Section
      id="button"
      title="Button"
      description="Brand button in the Onyx 'offset key' language — the notched key with a solid travel block down-right, pressed flush on :active with a springy bounce (the old pill + Framer scale-tap is gone, so every button presses the same way). default/gradient raised most (navy gradient, 3px) · secondary/destructive simpler (2px) · outline/ghost flat but press down 1px · link unchanged. 7 variants × 8 sizes, asChild, isLoading. The untouched shadcn button stays available via @aisquare/ui/stock."
    >
      <VariantBlock label="Variants">
        <Keyed>
          <Button>Default</Button>
          <Button variant="gradient">Gradient</Button>
          <Button variant="destructive">Destructive</Button>
          <Button variant="outline">Outline</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="link">Link</Button>
        </Keyed>
      </VariantBlock>

      <VariantBlock label="Sizes">
        <Keyed>
          <Button size="xs">xs</Button>
          <Button size="sm">sm</Button>
          <Button size="default">default</Button>
          <Button size="lg">lg</Button>
        </Keyed>
      </VariantBlock>

      <VariantBlock label="Icon sizes">
        <Keyed>
          <Button size="icon-xs" aria-label="add (xs)">
            <PlusIcon />
          </Button>
          <Button size="icon-sm" aria-label="add (sm)">
            <PlusIcon />
          </Button>
          <Button size="icon" aria-label="add (default)">
            <PlusIcon />
          </Button>
          <Button size="icon-lg" aria-label="add (lg)">
            <PlusIcon />
          </Button>
        </Keyed>
      </VariantBlock>

      <VariantBlock label="With leading / trailing icons">
        <Keyed>
          <Button>
            <PlusIcon />
            New experience
          </Button>
          <Button variant="outline">
            Next step
            <ChevronRightIcon />
          </Button>
        </Keyed>
      </VariantBlock>

      <VariantBlock label="States">
        <Keyed>
          <Button disabled>Disabled</Button>
          <Button variant="outline" disabled>
            Outline disabled
          </Button>
          <Button aria-invalid>Invalid (aria)</Button>
        </Keyed>
      </VariantBlock>
    </Section>
  );
}
